import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Trans, useTranslation } from 'react-i18next'

import { CheckIcon, CloseIcon, CopyIcon } from '../components/icons'
import { useFocusTrap } from '../hooks/useFocusTrap'
import { createShare, previewShare, type SharePreview, type ShareInfo } from '../lib/shares'

/**
 * Paylasim diyalogu.
 *
 * ## Ek uyarisi ATLANAMAZ
 *
 * Paylasilacak aralikta ek varsa, onay ONCESI ad ve boyut listesi + "bu
 * baglantiya sahip olan herkes bu dosyalara erisebilecek" uyarisi
 * gosteriliyor. Sunucu `include_attachments` alanini ZORUNLU tutarak bunu
 * yapisal olarak destekliyor: varsayilan olmadigi icin arayuz bir deger
 * secmek zorunda, yani soruyu atlayamiyor.
 *
 * ## Onizleme SUNUCUDAN
 *
 * Hangi ekler paylasilacak sorusunu istemcide hesaplamak mumkun ama yanlis:
 * aktif yolu ve ek baglarini IKINCI bir yerde yorumlamak demek - ve iki
 * yorum kacinilmaz olarak ayrisir. Ayrisirsa kullaniciya YANLIS bir liste
 * gostermis oluruz ve o, bir gizlilik hatasi.
 *
 * ## Paylasim "o ana kadar", dondurulmus
 *
 * Diyalog bunu ACIKCA soyluyor. Kullanicinin canli bir paylasim beklemesi en
 * olasi yanlis varsayim; sessiz kalmak onu duzeltmiyor.
 */
export function ShareDialog({
  conversationId,
  onClose,
}: {
  conversationId: string
  onClose(): void
}) {
  const { t } = useTranslation()
  const container = useRef<HTMLDivElement>(null)
  const [preview, setPreview] = useState<SharePreview | null>(null)
  const [includeAttachments, setIncludeAttachments] = useState(false)
  const [allowFork, setAllowFork] = useState(true)
  const [requireLogin, setRequireLogin] = useState(false)
  // Varsayilan KAPALI: baglantiya sahip herkese acik bir sayfada adini
  // yaymak, kullanicinin acikca istemedigi bir kimlik ifsasi olurdu.
  const [showOwner, setShowOwner] = useState(false)
  const [password, setPassword] = useState('')
  const [result, setResult] = useState<ShareInfo | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  // `t`yi ref uzerinden: onizleme efektinin bagimliligi olmasi, dil
  // degistiginde onizlemeyi YENIDEN CEKMEK demek olurdu.
  const tRef = useRef(t)
  tRef.current = t

  useFocusTrap(container, { active: true, onClose })

  useEffect(() => {
    let alive = true
    previewShare(conversationId)
      .then((value) => {
        if (alive) setPreview(value)
      })
      .catch(() => {
        if (alive) setError(tRef.current('share.error.previewFailed'))
      })
    return () => {
      alive = false
    }
  }, [conversationId])

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      const created = await createShare(conversationId, {
        include_attachments: includeAttachments,
        allow_fork: allowFork,
        require_login: requireLogin,
        show_owner: showOwner,
        password: password.trim() || null,
      })
      setResult(created)
    } catch {
      setError(t('share.error.createFailed'))
    } finally {
      setBusy(false)
    }
  }

  async function copyLink(url: string) {
    // `execCommand` fallback KORUNUYOR: LAN'da duz HTTP kullanildiginda
    // `navigator.clipboard` UNDEFINED oluyor ve kopyalama sessizce
    // calismiyor. Vanilla arayuzde bu yasandi.
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url)
      } else {
        const field = document.createElement('textarea')
        field.value = url
        document.body.appendChild(field)
        field.select()
        document.execCommand('copy')
        document.body.removeChild(field)
      }
      setCopied(true)
    } catch {
      setError(t('share.error.copyFailed'))
    }
  }

  const attachments = preview?.attachments ?? []

  // Baglanti KULLANICININ su an bulundugu adresle kuruluyor. Sunucunun
  // dondurdugu `url` `HEDEFAI_PUBLIC_ORIGIN`den uretiliyor ve LAN'da ayni
  // uygulama birden fazla adresten (IP, `hedefai.local`, bilgisayar adi)
  // aciliyor: sabit origin, sayfayi hangi adresten actiysan ONUNLA
  // calismayan bir baglanti veriyordu (gozlenen: eski makinenin IP'si).
  const shareUrl = result ? `${window.location.origin}/s/${result.slug}` : ''

  return createPortal(
    <div
      className="palette-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        className="share-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-title"
        ref={container}
        tabIndex={-1}
      >
        {/* Kapatma SAG USTTE: modal kapatmanin bilinen yeri; alttaki "Kapat"
            dugmesi uzun bir diyalogda ekranin disinda kaliyordu. */}
        <button
          type="button"
          className="share-close"
          onClick={onClose}
          aria-label={t('common.close')}
          title={t('common.close')}
        >
          <CloseIcon size={18} />
        </button>
        <h2 id="share-title">{t('share.dialog.title')}</h2>

        {result === null ? (
          <>
            <p className="share-note">
              {preview === null
                ? t('share.dialog.preparing')
                : t('share.dialog.messageCount', { count: preview.message_count })}
            </p>
            {/*
              DONDURULMUS oldugunu acikca soyluyoruz: kullanicinin canli bir
              paylasim beklemesi en olasi yanlis varsayim ve sessiz kalmak
              onu duzeltmiyor.
            */}
            <p className="share-note share-note-dim">
              {/*
                `<Trans>`: vurgu cumlenin ORTASINDA. Parcalara bolup arasina
                `<strong>` koymak, kelime sirasi farkli bir dilde parcalari
                yanlis sirada dizerdi.
              */}
              <Trans i18nKey="share.dialog.frozen" components={{ strong: <strong /> }} />
            </p>

            {attachments.length > 0 && (
              <section className="share-attachments" aria-labelledby="share-att-title">
                <h3 id="share-att-title">
                  {t('share.dialog.fileCount', { count: attachments.length })}
                </h3>
                <ul>
                  {attachments.map((item) => (
                    <li key={item.id}>
                      <span className="share-file">{item.filename}</span>
                      <span className="share-size">{formatSize(item.byte_size)}</span>
                    </li>
                  ))}
                </ul>
                <label className="share-check share-warn">
                  <input
                    type="checkbox"
                    checked={includeAttachments}
                    onChange={(event) => setIncludeAttachments(event.target.checked)}
                  />
                  <span>
                    <Trans
                      i18nKey="share.dialog.includeFiles"
                      components={{ strong: <strong /> }}
                    />
                  </span>
                </label>
              </section>
            )}

            <label className="share-check">
              <input
                type="checkbox"
                checked={allowFork}
                onChange={(event) => setAllowFork(event.target.checked)}
              />
              <span>{t('share.dialog.allowFork')}</span>
            </label>

            <label className="share-check">
              <input
                type="checkbox"
                checked={requireLogin}
                onChange={(event) => setRequireLogin(event.target.checked)}
              />
              <span>{t('share.dialog.requireLogin')}</span>
            </label>

            {/*
              Adin gorunmesi kullanicinin KENDI tercihi: isaretlenmediginde
              sunucu adi yanita hic koymuyor (bkz. `shares.owner_name`).
            */}
            <label className="share-check">
              <input
                type="checkbox"
                checked={showOwner}
                onChange={(event) => setShowOwner(event.target.checked)}
              />
              <span>{t('share.dialog.showOwner')}</span>
            </label>

            <label className="share-field">
              <span>{t('share.dialog.passwordLabel')}</span>
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                placeholder={t('share.dialog.passwordPlaceholder')}
              />
            </label>

            {error && (
              <p className="share-error" role="alert">
                {error}
              </p>
            )}

            <footer className="share-actions">
              <button type="button" className="copy" onClick={onClose}>
                {t('share.dialog.cancel')}
              </button>
              <button type="button" onClick={() => void submit()} disabled={busy || !preview}>
                {busy ? t('share.dialog.creating') : t('share.dialog.create')}
              </button>
            </footer>
          </>
        ) : (
          <>
            <p className="share-note">{t('share.dialog.ready')}</p>
            <div className="share-link">
              {/* `readOnly` + otomatik secim: kullanici elle de kopyalayabilsin. */}
              <input
                type="text"
                value={shareUrl}
                readOnly
                onFocus={(event) => event.currentTarget.select()}
                aria-label={t('share.dialog.linkLabel')}
              />
              {/* Ikon: metin dugmesi ("Kopyala"/"Kopyalandı") dile gore
                  genisleyip baglanti alanini daraltiyordu. */}
              <button
                type="button"
                className="share-copy"
                onClick={() => void copyLink(shareUrl)}
                aria-label={copied ? t('common.copied') : t('share.dialog.copy')}
                title={copied ? t('common.copied') : t('share.dialog.copy')}
                aria-live="polite"
              >
                {copied ? <CheckIcon size={17} /> : <CopyIcon size={17} />}
              </button>
            </div>
            <p className="share-note share-note-dim">
              {t(
                result.include_attachments
                  ? 'share.dialog.summaryWithFiles'
                  : 'share.dialog.summaryWithoutFiles',
                { count: result.message_count },
              )}
              {result.has_password && ` ${t('share.dialog.passwordProtected')}`}
            </p>
            {error && (
              <p className="share-error" role="alert">
                {error}
              </p>
            )}
          </>
        )}
      </div>
    </div>,
    document.body,
  )
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
